import { toast } from "@/hooks/useToast";
import CartService from "./cartService";
import { useCartStore } from "@/stores/cartStore";

// 🎯 TIPOS PARA EL SISTEMA DE COLA
export interface CartAction {
  id: string;
  type: "ADD_ITEM" | "REMOVE_ITEM" | "UPDATE_QUANTITY" | "CLEAR_CART";
  data: any;
  timestamp: number;
  retries: number;
  maxRetries: number;
  status: "PENDING" | "PROCESSING" | "SUCCESS" | "FAILED";
  error?: string;
}

export interface QueueMetrics {
  totalActions: number;
  successfulActions: number;
  failedActions: number;
  averageProcessingTime: number;
  queueLength: number;
}

export interface CartQueueConfig {
  maxRetries: number;
  retryDelay: number;
  maxQueueSize: number;
  enableMetrics: boolean;
}

// 🚀 CART QUEUE SERVICE
export class CartQueueService {
  private queue: CartAction[] = [];
  private processing = false;
  private currentActionId: string | null = null; // Para tracking de acción específica
  private metrics: QueueMetrics = {
    totalActions: 0,
    successfulActions: 0,
    failedActions: 0,
    averageProcessingTime: 0,
    queueLength: 0,
  };
  private config: CartQueueConfig;
  private processingTimes: number[] = [];
  private listeners: Array<() => void> = []; // Listeners para cambios de estado

  constructor(config: Partial<CartQueueConfig> = {}) {
    this.config = {
      maxRetries: 3,
      retryDelay: 1000,
      maxQueueSize: 100,
      enableMetrics: true,
      ...config,
    };

    // 🔄 SINCRONIZAR SESIÓN ANÓNIMA EXISTENTE
    this.syncAnonymousSession();
  }

  // ------------------------------
  // METODOS EXPUESTOS PRINICIAPLES DE OPERACIONES DEL CARRITO
  // - Integran la logica de la cola Y el optimistc update
  // ------------------------------

  // 🎯 AGREGAR ITEM AL CARRO (CON OPTIMISTIC UPDATE)
  async addItem(item: any): Promise<void> {
    const action: CartAction = {
      id: this.generateId(),
      type: "ADD_ITEM",
      data: { productId: item.id, quantity: 1, item },
      timestamp: Date.now(),
      retries: 0,
      maxRetries: this.config.maxRetries,
      status: "PENDING",
    };

    // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
    this.updateUIOptimistic(action);

    // Agregar a la cola
    this.addToQueue(action);

    // Procesar en background
    this.processQueue();
  }

  // 🎯 REMOVER ITEM DEL CARRO
  async removeItem(cartItemId: string): Promise<void> {
    const action: CartAction = {
      id: this.generateId(),
      type: "REMOVE_ITEM",
      data: { cartItemId },
      timestamp: Date.now(),
      retries: 0,
      maxRetries: this.config.maxRetries,
      status: "PENDING",
    };

    // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
    this.updateUIOptimistic(action);

    // Agregar a la cola
    this.addToQueue(action);

    // Procesar en background
    this.processQueue();
  }

  // 🎯 ACTUALIZAR CANTIDAD
  async updateQuantity(cartItemId: string, quantity: number): Promise<void> {
    const action: CartAction = {
      id: this.generateId(),
      type: "UPDATE_QUANTITY",
      data: { cartItemId, quantity },
      timestamp: Date.now(),
      retries: 0,
      maxRetries: this.config.maxRetries,
      status: "PENDING",
    };

    // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
    this.updateUIOptimistic(action);

    // Agregar a la cola
    this.addToQueue(action);

    // Procesar en background
    this.processQueue();
  }

  // 🎯 LIMPIAR CARRO
  async clearCart(): Promise<void> {
    const action: CartAction = {
      id: this.generateId(),
      type: "CLEAR_CART",
      data: {},
      timestamp: Date.now(),
      retries: 0,
      maxRetries: this.config.maxRetries,
      status: "PENDING",
    };

    // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
    this.updateUIOptimistic(action);

    // Agregar a la cola
    this.addToQueue(action);

    // Procesar en background
    this.processQueue();
  }

  // ------------------------------
  // METODOS PRIVADOS
  // ------------------------------

  // 🔄 AGREGAR A LA COLA
  private addToQueue(action: CartAction): void {
    if (this.queue.length >= this.config.maxQueueSize) {
      console.warn("⚠️ Cola llena, descartando acción más antigua");
      this.queue.shift(); // Remover acción más antigua
    }

    this.queue.push(action);
    this.metrics.totalActions++;
    this.metrics.queueLength = this.queue.length;

    console.log(`📥 Acción agregada a la cola: ${action.type}`, {
      id: action.id,
      queueLength: this.queue.length,
    });

    // Notificar cambio de estado
    this.notifyListeners();
  }

  // 🔄 PROCESAR COLA
  private async processQueue(): Promise<void> {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;
    console.log(`🔄 Procesando cola: ${this.queue.length} acciones pendientes`);

    while (this.queue.length > 0) {
      const action = this.queue[0];
      const startTime = Date.now();

      // Marcar acción actual para tracking
      this.currentActionId = action.id;

      try {
        // Marcar como procesando
        action.status = "PROCESSING";

        // Ejecutar acción
        await this.executeAction(action);

        // Marcar como exitosa
        action.status = "SUCCESS";
        this.metrics.successfulActions++;

        // Remover de la cola
        this.queue.shift();

        // Actualizar métricas
        const processingTime = Date.now() - startTime;
        this.updateMetrics(processingTime);

        console.log(`✅ Acción completada: ${action.type}`, {
          id: action.id,
          processingTime,
          queueLength: this.queue.length,
        });

        // Notificar cambio de estado inmediatamente
        this.notifyListeners();
      } catch (error) {
        console.error(`❌ Error procesando acción: ${action.type}`, {
          id: action.id,
          error: error instanceof Error ? error.message : "Error desconocido",
          retries: action.retries,
        });

        // Manejar reintentos
        if (action.retries < action.maxRetries) {
          action.retries++;
          action.status = "PENDING";
          action.error =
            error instanceof Error ? error.message : "Error desconocido";

          // Mover al final de la cola para reintento
          this.queue.shift();
          this.queue.push(action);

          // Esperar antes del reintento (backoff exponencial)
          const delay =
            this.config.retryDelay * Math.pow(2, action.retries - 1);
          await this.delay(delay);

          console.log(
            `🔄 Reintentando acción: ${action.type} (intento ${action.retries}/${action.maxRetries})`
          );
        } else {
          // Máximo de reintentos alcanzado
          action.status = "FAILED";
          action.error =
            error instanceof Error ? error.message : "Error desconocido";
          this.metrics.failedActions++;

          // Remover de la cola
          this.queue.shift();

          // Mostrar error al usuario
          this.showErrorToUser(action);

          console.error(`💥 Acción fallida definitivamente: ${action.type}`, {
            id: action.id,
            error: action.error,
          });

          // Notificar cambio de estado inmediatamente
          this.notifyListeners();
        }
      } finally {
        // Limpiar acción actual
        this.currentActionId = null;
      }
    }

    this.processing = false;
    console.log("✅ Cola procesada completamente");
  }

  // 🎯 EJECUTAR ACCIÓN ESPECÍFICA
  private async executeAction(action: CartAction): Promise<void> {
    switch (action.type) {
      case "ADD_ITEM":
        await this.executeAddItem(action);
        break;
      case "REMOVE_ITEM":
        await this.executeRemoveItem(action);
        break;
      case "UPDATE_QUANTITY":
        await this.executeUpdateQuantity(action);
        break;
      case "CLEAR_CART":
        await this.executeClearCart();
        break;
      default:
        throw new Error(`Tipo de acción no soportado: ${action.type}`);
    }
  }

  // 🎯 EJECUTAR AGREGAR ITEM
  private async executeAddItem(action: CartAction): Promise<void> {
    const { productId, quantity } = action.data;

    // Determinar si es usuario anónimo o autenticado
    const isAnonymous = !localStorage.getItem("accessToken");
    console.log("🔑 isAnonymous", isAnonymous);

    if (isAnonymous) {
      console.log("creando carrito anonimo");
      // Usuario anónimo - manejar sesión automáticamente
      let sessionToken = localStorage.getItem("anonymousSessionToken");

      if (!sessionToken) {
        // Generar sesión anónima si no existe
        try {
          const sessionData = await CartService.generateAnonymousSession();
          sessionToken = sessionData.sessionToken;
          localStorage.setItem("anonymousSessionToken", sessionToken);

          // 🔄 SINCRONIZAR CON EL STORE
          const { setAnonymousSessionToken, setIsAnonymous } =
            useCartStore.getState();
          setAnonymousSessionToken(sessionToken);
          setIsAnonymous(true);

          console.log("🔑 Sesión anónima generada automáticamente");
        } catch (error) {
          throw new Error("No se pudo generar sesión anónima");
        }
      } else {
        // 🔄 SINCRONIZAR SESIÓN EXISTENTE CON EL STORE
        const { setAnonymousSessionToken, setIsAnonymous } =
          useCartStore.getState();
        setAnonymousSessionToken(sessionToken);
        setIsAnonymous(true);
        console.log("🔄 Sesión anónima existente sincronizada");
      }

      await CartService.addToAnonymousCart(
        { productId, quantity },
        sessionToken
      );
    } else {
      // Usuario autenticado
      console.log("creando carrito autenticado");
      const sessionToken = localStorage.getItem("accessToken");
      if (!sessionToken) {
        throw new Error("No hay sesión de usuario disponible");
      }
      await CartService.addToCart({ productId, quantity });
    }
  }

  // 🎯 EJECUTAR REMOVER ITEM
  private async executeRemoveItem(action: CartAction): Promise<void> {
    const { cartItemId } = action.data;
    const isAnonymous = !localStorage.getItem("accessToken");

    if (isAnonymous) {
      const sessionToken = localStorage.getItem("anonymousSessionToken");
      if (!sessionToken) {
        throw new Error("No hay sesión anónima disponible");
      }

      await CartService.removeAnonymousCartItem(cartItemId, sessionToken);
    } else {
      const sessionToken = localStorage.getItem("accessToken");
      if (!sessionToken) {
        throw new Error("No hay sesión de usuario disponible");
      }
      await CartService.removeCartItem(cartItemId);
    }
  }

  // 🎯 EJECUTAR ACTUALIZAR CANTIDAD
  private async executeUpdateQuantity(action: CartAction): Promise<void> {
    const { cartItemId, quantity } = action.data;
    const isAnonymous = !localStorage.getItem("accessToken");

    if (isAnonymous) {
      const sessionToken = localStorage.getItem("anonymousSessionToken");
      if (!sessionToken) {
        throw new Error("No hay sesión anónima disponible");
      }

      await CartService.updateAnonymousCartItemQuantity(
        cartItemId,
        { quantity },
        sessionToken
      );
    } else {
      const sessionToken = localStorage.getItem("accessToken");
      if (!sessionToken) {
        throw new Error("No hay sesión de usuario disponible");
      }
      await CartService.updateCartItemQuantity(
        cartItemId,
        { quantity }
      );
    }
  }

  // 🎯 EJECUTAR LIMPIAR CARRO
  private async executeClearCart(): Promise<void> {
    const isAnonymous = !localStorage.getItem("accessToken");

    if (isAnonymous) {
      const sessionToken = localStorage.getItem("anonymousSessionToken");
      if (!sessionToken) {
        throw new Error("No hay sesión anónima disponible");
      }

      await CartService.clearAnonymousCart(sessionToken);
    } else {
      // Para usuarios autenticados, necesitamos el userId
      const userId = localStorage.getItem("userId");
      if (!userId) {
        throw new Error("No hay userId disponible");
      }
      const sessionToken = localStorage.getItem("accessToken");
      if (!sessionToken) {
        throw new Error("No hay sesión de usuario disponible");
      }
      await CartService.clearCart(userId);
    }
  }

  // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
  private updateUIOptimistic(action: CartAction): void {
    // Esta función se integrará con el cartStore para actualizar la UI inmediatamente
    // La implementación específica dependerá de cómo esté estructurado el cartStore
    console.log(`🚀 Optimistic update para: ${action.type}`, action.data);

    // 🎯 NOTA: Los optimistic updates se manejan en el hook useCartQueue
    // que tiene acceso directo al cartStore. Este método es solo para logging.
  }

  // 📊 ACTUALIZAR MÉTRICAS
  private updateMetrics(processingTime: number): void {
    if (!this.config.enableMetrics) return;

    this.processingTimes.push(processingTime);

    // Mantener solo los últimos 100 tiempos para el promedio
    if (this.processingTimes.length > 100) {
      this.processingTimes.shift();
    }

    this.metrics.averageProcessingTime =
      this.processingTimes.reduce((sum, time) => sum + time, 0) /
      this.processingTimes.length;
    this.metrics.queueLength = this.queue.length;
  }

  // 🎯 MOSTRAR ERROR AL USUARIO
  private showErrorToUser(action: CartAction): void {
    let title = "Error en el carrito";
    let description = "No se pudo completar la operación. Inténtalo de nuevo.";

    switch (action.type) {
      case "ADD_ITEM":
        title = "Error al agregar producto";
        description =
          "No se pudo agregar el producto al carrito. Inténtalo de nuevo.";
        break;
      case "REMOVE_ITEM":
        title = "Error al remover producto";
        description =
          "No se pudo remover el producto del carrito. Inténtalo de nuevo.";
        break;
      case "UPDATE_QUANTITY":
        title = "Error al actualizar cantidad";
        description = "No se pudo actualizar la cantidad. Inténtalo de nuevo.";
        break;
      case "CLEAR_CART":
        title = "Error al limpiar carrito";
        description = "No se pudo limpiar el carrito. Inténtalo de nuevo.";
        break;
    }

    toast({
      title,
      description,
      variant: "destructive",
    });
  }

  // 🎯 OBTENER MÉTRICAS
  getMetrics(): QueueMetrics {
    return { ...this.metrics };
  }

  // 🎯 OBTENER ESTADO DE LA COLA
  getQueueStatus(): {
    isProcessing: boolean;
    queueLength: number;
    pendingActions: CartAction[];
    currentActionId: string | null;
  } {
    return {
      isProcessing: this.processing,
      queueLength: this.queue.length,
      pendingActions: [...this.queue],
      currentActionId: this.currentActionId,
    };
  }

  // 🎯 LIMPIAR COLA
  clearQueue(): void {
    this.queue = [];
    this.processing = false;
    console.log("🧹 Cola limpiada");
  }

  // 🎯 UTILIDADES
  private generateId(): string {
    return `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * 🔄 Sincroniza la sesión anónima entre localStorage y el store
   */
  private syncAnonymousSession(): void {
    const sessionToken = localStorage.getItem("anonymousSessionToken");
    if (sessionToken) {
      const { setAnonymousSessionToken, setIsAnonymous } =
        useCartStore.getState();
      setAnonymousSessionToken(sessionToken);
      setIsAnonymous(true);
      console.log("🔄 Sesión anónima sincronizada desde localStorage");
    }
  }

  /**
   * 🔔 Suscribirse a cambios de estado de la cola
   */
  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    // Retornar función para desuscribirse
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * 🔔 Notificar a todos los listeners de cambios
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener());
  }
}

// 🎯 INSTANCIA GLOBAL DEL CART QUEUE
export const cartQueue = new CartQueueService({
  maxRetries: 3,
  retryDelay: 1000,
  maxQueueSize: 50,
  enableMetrics: true,
});
